import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import {
  MousePointerClick,
  X as XIcon,
  Plus as PlusIcon,
  FlaskConical,
  Info,
  Settings2,
  Ruler,
  ListChecks,
  ShieldCheck,
  Calendar,
  FileText,
  Heading,
  Camera,
  MapPin,
  PenTool,
  Barcode,
  Repeat,
  Tag as TagIcon,
  Minus as DividerIcon,
  Mic,
  GitBranch,
} from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { useRole } from '../../hooks/useRole';
import { ThemedText, Button, Input } from '../';
import { StyledSelect } from '../FormStyles';
import { COMPONENT_LIBRARY } from './ComponentLibrary';
import formSchemaService from '../../api/services/formSchemaService';
import { synthesizeLabResultFields } from '../../utils/labResultFieldSynthesis';
import useLabAnalyteSuggestions from '../../hooks/useLabAnalyteSuggestions';
import {
  PropertySection,
  PropertyField,
  BooleanRow,
  OptionsEditor,
  ValidationRuleEditor,
  FieldRulesEditor,
} from './FieldProperties';

// ── Panel-local styled primitives ──────────────────────────────────────────

const PropertiesContainer = styled.div`
  padding: ${props => props.theme.spacing.md};
  height: 100%;
  overflow-y: auto;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme.spacing.sm};
  padding: ${props => props.theme.spacing.xl};
  text-align: center;
  color: ${props => props.theme.colors.muted};
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  padding: ${props => props.theme.spacing.sm} 0 ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.md};
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const FieldTypeBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: ${props => props.theme.borderRadius.md};
  background: ${props => props.theme.colors.tint}20;
  color: ${props => props.theme.colors.tint};
  flex-shrink: 0;
`;

const HeaderText = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const HeaderTitle = styled.div`
  font-size: ${props => props.theme.typography.sizes.medium};
  font-weight: ${props => props.theme.typography.weights.bold};
  color: ${props => props.theme.colors.text};
  line-height: 1.2;
`;

const HeaderSubtitle = styled.div`
  font-size: ${props => props.theme.typography.sizes.small};
  color: ${props => props.theme.colors.muted};
`;

const SegmentedControl = styled.div`
  display: flex;
  background: ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: 3px;
  gap: 2px;
`;

const SegmentButton = styled.button`
  flex: 1;
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border: none;
  border-radius: calc(${props => props.theme.borderRadius.md} - 2px);
  cursor: pointer;
  font-size: ${props => props.theme.typography.sizes.small};
  font-weight: ${props => props.$active ? '600' : '400'};
  background: ${props => props.$active ? props.theme.colors.primary : 'transparent'};
  color: ${props => props.$active ? '#ffffff' : props.theme.colors.muted};
  transition: background 0.15s, color 0.15s;
  white-space: nowrap;

  &:hover {
    background: ${props => props.$active ? props.theme.colors.primary : props.theme.colors.backgroundSecondary};
    color: ${props => props.$active ? '#ffffff' : props.theme.colors.text};
  }
`;

const InlineRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing.md};
`;

const InputCompact = styled.div`
  /* Remove the default margin-bottom from Input's container in compact rows. */
  & > div { margin-bottom: 0; }
`;

// ── Icon resolution per field ──────────────────────────────────────────────

const fieldTypeIcon = (field) => {
  if (!field) return Info;
  if (field.componentId === 'sample_id') return FlaskConical;
  switch (field.type) {
    case 'number': return Ruler;
    case 'select': return ListChecks;
    case 'textarea': return FileText;
    case 'datetime': return Calendar;
    case 'checkbox_group':
    case 'radio': return ListChecks;
    case 'tags': return TagIcon;
    case 'image_camera': return Camera;
    case 'geolocation': return MapPin;
    case 'drawing': return PenTool;
    case 'voice': return Mic;
    case 'qr_scanner': return ShieldCheck;
    case 'barcode_scanner': return Barcode;
    case 'repeated_form': return Repeat;
    case 'header': return Heading;
    case 'divider': return DividerIcon;
    default: return Info;
  }
};

// ── Main component ─────────────────────────────────────────────────────────

export const FieldPropertiesPanel = ({ field, onUpdate, generateFieldName, allFields, fields, onFieldsReplace, currentFormId }) => {
  const theme = useTheme();
  const { user } = useAuth();
  const { isSuperAdmin } = useRole();
  const [repeatedFormSchemas, setRepeatedFormSchemas] = useState([]);
  const [analyteDraft, setAnalyteDraft] = useState('');
  const [analyteUnitDraft, setAnalyteUnitDraft] = useState('');
  const [analyteTypeDraft, setAnalyteTypeDraft] = useState('Number');
  const { suggestions: analyteSuggestions } = useLabAnalyteSuggestions(
    analyteDraft,
    { enabled: field?.componentId === 'sample_id', limit: 8 }
  );

  useEffect(() => {
    const loadRepeatedFormSchemas = async () => {
      try {
        const response = await formSchemaService.getFormSchemas({ includeHiddenForms: true, pageSize: 500 });
        setRepeatedFormSchemas(response.items || []);
      } catch (error) {
        console.error('Failed to load repeated form schemas:', error);
      }
    };
    loadRepeatedFormSchemas();
  }, []);

  if (!field) {
    return (
      <PropertiesContainer>
        <EmptyState>
          <MousePointerClick size={40} />
          <ThemedText type="defaultSemiBold">Select a Field</ThemedText>
          <ThemedText type="small" style={{ color: theme.colors.muted, maxWidth: 260 }}>
            Click on a field in the form canvas to edit its properties.
          </ThemedText>
        </EmptyState>
      </PropertiesContainer>
    );
  }

  const component = COMPONENT_LIBRARY.find(c => c.id === field.componentId);
  const { props } = field;
  const TypeIcon = fieldTypeIcon(field);

  const handlePropChange = (propName, value) => {
    onUpdate({ ...field, props: { ...field.props, [propName]: value } });
  };

  const handleLabelChange = (newLabel) => {
    const updates = { label: newLabel };
    if (!props.nameManuallySet && generateFieldName) {
      updates.name = generateFieldName(newLabel);
    }
    onUpdate({ ...field, props: { ...field.props, ...updates } });
  };

  const handleNameChange = (newName) => {
    onUpdate({ ...field, props: { ...field.props, name: newName, nameManuallySet: true } });
  };

  const handleConfigChange = (patch) => {
    handlePropChange('configuration', { ...(props.configuration || {}), ...patch });
  };

  const isDividerOrHeader = field.type === 'divider' || field.type === 'header';
  const showAdvanced = field.componentId !== 'sample_id' && !['divider', 'header', 'spacer'].includes(field.type);
  const showOptionsEditor = ['select', 'radio', 'checkbox_group', 'tags'].includes(field.type);

  // Field Rules — gated like other advanced sections (not on pure layout fields).
  const showFieldRules = !['divider', 'header', 'spacer'].includes(field.type);
  // Sibling fields available as rule triggers: exclude the current field and
  // layout-only fields (they hold no value to test against).
  const ruleSiblingFields = (Array.isArray(fields) ? fields : [])
    .filter(f => f.id !== field.id && !['divider', 'header', 'spacer'].includes(f.type))
    .map(f => ({ id: f.id, label: f.props?.label || f.id, type: f.type, props: f.props }));

  // ── Basics section contents ──────────────────────────────────────────────

  const basicsContents = (
    <>
      {props.hasOwnProperty('label') && (
        <PropertyField>
          <InputCompact>
            <Input
              label="Field Label"
              value={props.label || ''}
              onChange={(e) => handleLabelChange(e.target.value)}
              placeholder="Enter field label"
            />
          </InputCompact>
        </PropertyField>
      )}

      {props.hasOwnProperty('name') && (
        <PropertyField
          help={isSuperAdmin()
            ? 'Used for database storage. Use lowercase letters, numbers, and underscores only.'
            : 'Field name is auto-generated from the label. Contact an administrator to change it.'}
        >
          <InputCompact>
            <Input
              label="Field Name (Database)"
              value={props.name || ''}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="field_name"
              disabled={!isSuperAdmin()}
            />
          </InputCompact>
        </PropertyField>
      )}

      {props.hasOwnProperty('placeholder') && !['drawing', 'image_camera', 'geolocation'].includes(field.type) && (
        <PropertyField>
          <InputCompact>
            <Input
              label="Placeholder Text"
              value={props.placeholder || ''}
              onChange={(e) => handlePropChange('placeholder', e.target.value)}
              placeholder="Enter placeholder text"
            />
          </InputCompact>
        </PropertyField>
      )}

      {props.hasOwnProperty('required') && (
        <BooleanRow
          label="Required field"
          description="Users must fill in this field before submitting."
          checked={!!props.required}
          onChange={(next) => handlePropChange('required', next)}
        />
      )}
    </>
  );

  return (
    <PropertiesContainer>
      <PanelHeader>
        <FieldTypeBadge>
          <TypeIcon size={22} />
        </FieldTypeBadge>
        <HeaderText>
          <HeaderTitle>{component?.label || 'Field'}</HeaderTitle>
          <HeaderSubtitle>Edit field properties</HeaderSubtitle>
        </HeaderText>
      </PanelHeader>

      {/* Basics — always first, except for pure layout fields */}
      {(props.hasOwnProperty('label') || props.hasOwnProperty('name') || props.hasOwnProperty('placeholder') || props.hasOwnProperty('required')) && (
        <PropertySection icon={Info} title="Basics">
          {basicsContents}
        </PropertySection>
      )}

      {/* Type-specific sections */}

      {field.type === 'datetime' && (
        <PropertySection icon={Calendar} title="Date & Time" description="Choose what to capture and whether it fills automatically.">
          <PropertyField label="Precision">
            <SegmentedControl>
              <SegmentButton $active={props.precision === 'date'} onClick={() => handlePropChange('precision', 'date')} type="button">Date</SegmentButton>
              <SegmentButton $active={props.precision === 'time'} onClick={() => handlePropChange('precision', 'time')} type="button">Time</SegmentButton>
              <SegmentButton $active={!props.precision || props.precision === 'datetime'} onClick={() => handlePropChange('precision', 'datetime')} type="button">Date &amp; Time</SegmentButton>
            </SegmentedControl>
          </PropertyField>

          {props.hasOwnProperty('autoCapture') && (
            <BooleanRow
              label={`Auto-capture current ${props.precision === 'date' ? 'date' : props.precision === 'time' ? 'time' : 'date & time'}`}
              description="Automatically fills when the form loads."
              checked={!!props.autoCapture}
              onChange={(next) => handlePropChange('autoCapture', next)}
            />
          )}
        </PropertySection>
      )}

      {field.type === 'number' && (
        <PropertySection icon={Ruler} title="Constraints" description="Set allowed range. Leave blank for no limit.">
          <InlineRow>
            <InputCompact>
              <Input
                label="Minimum"
                type="number"
                value={props.min ?? ''}
                onChange={(e) => handlePropChange('min', e.target.value === '' ? null : parseFloat(e.target.value))}
                placeholder="No minimum"
              />
            </InputCompact>
            <InputCompact>
              <Input
                label="Maximum"
                type="number"
                value={props.max ?? ''}
                onChange={(e) => handlePropChange('max', e.target.value === '' ? null : parseFloat(e.target.value))}
                placeholder="No maximum"
              />
            </InputCompact>
          </InlineRow>
        </PropertySection>
      )}

      {field.type === 'textarea' && (
        <PropertySection icon={FileText} title="Text Area">
          <PropertyField help="How many rows of text to display by default (1–10).">
            <InputCompact>
              <Input
                label="Rows"
                type="number"
                value={props.rows ?? 3}
                onChange={(e) => handlePropChange('rows', parseInt(e.target.value) || 3)}
                min="1"
                max="10"
              />
            </InputCompact>
          </PropertyField>
        </PropertySection>
      )}

      {field.type === 'header' && (
        <PropertySection icon={Heading} title="Heading">
          <PropertyField>
            <InputCompact>
              <Input
                label="Header Text"
                value={props.text || ''}
                onChange={(e) => handlePropChange('text', e.target.value)}
                placeholder="Enter header text"
              />
            </InputCompact>
          </PropertyField>

          <InlineRow>
            <PropertyField label="Level">
              <StyledSelect value={props.level || 'h3'} onChange={(e) => handlePropChange('level', e.target.value)}>
                <option value="h1">H1 (Largest)</option>
                <option value="h2">H2</option>
                <option value="h3">H3</option>
                <option value="h4">H4</option>
                <option value="h5">H5</option>
                <option value="h6">H6 (Smallest)</option>
              </StyledSelect>
            </PropertyField>
            <PropertyField label="Alignment">
              <StyledSelect value={props.align || 'left'} onChange={(e) => handlePropChange('align', e.target.value)}>
                <option value="left">Left</option>
                <option value="center">Center</option>
                <option value="right">Right</option>
              </StyledSelect>
            </PropertyField>
          </InlineRow>
        </PropertySection>
      )}

      {showOptionsEditor && (
        <PropertySection
          icon={field.type === 'tags' ? TagIcon : ListChecks}
          title={field.type === 'tags' ? 'Tag Options' : 'Options'}
          description="Each option has a database value and a user-facing label."
        >
          <OptionsEditor
            options={props.options || []}
            onChange={(next) => handlePropChange('options', next)}
            supportsColor={field.type === 'tags'}
            supportsGroup={['select', 'radio', 'checkbox_group'].includes(field.type)}
          />
        </PropertySection>
      )}

      {field.type === 'tags' && (
        <PropertySection icon={Settings2} title="Tag Settings">
          <BooleanRow
            label="Allow custom tags"
            description="Let users type in tags that aren't in the predefined options."
            checked={props.allowCustom !== false}
            onChange={(next) => handlePropChange('allowCustom', next)}
          />
          <PropertyField label="Maximum tags" help="Leave blank for no limit.">
            <InputCompact>
              <Input
                type="number"
                placeholder="e.g. 5"
                value={props.maxTags ?? ''}
                onChange={(e) => handlePropChange('maxTags', e.target.value === '' ? null : Number(e.target.value))}
              />
            </InputCompact>
          </PropertyField>
        </PropertySection>
      )}

      {field.type === 'image_camera' && (
        <PropertySection icon={Camera} title="Camera">
          <PropertyField>
            <InputCompact>
              <Input
                label="Placeholder Text"
                value={props.placeholder || ''}
                onChange={(e) => handlePropChange('placeholder', e.target.value)}
                placeholder="Tap to capture photo..."
              />
            </InputCompact>
          </PropertyField>
        </PropertySection>
      )}

      {field.type === 'geolocation' && (
        <PropertySection icon={MapPin} title="Location">
          <PropertyField>
            <InputCompact>
              <Input
                label="Placeholder Text"
                value={props.placeholder || ''}
                onChange={(e) => handlePropChange('placeholder', e.target.value)}
                placeholder="Tap to capture location..."
              />
            </InputCompact>
          </PropertyField>
          <PropertyField label="GPS Accuracy" help="Higher accuracy takes longer and uses more battery.">
            <StyledSelect value={props.accuracy || 'high'} onChange={(e) => handlePropChange('accuracy', e.target.value)}>
              <option value="low">Low (1 km)</option>
              <option value="medium">Medium (100 m)</option>
              <option value="high">High (10 m)</option>
            </StyledSelect>
          </PropertyField>
        </PropertySection>
      )}

      {field.type === 'drawing' && (
        <PropertySection icon={PenTool} title="Canvas">
          <PropertyField>
            <InputCompact>
              <Input
                label="Placeholder Text"
                value={props.placeholder || ''}
                onChange={(e) => handlePropChange('placeholder', e.target.value)}
                placeholder="Tap to draw or add signature..."
              />
            </InputCompact>
          </PropertyField>
        </PropertySection>
      )}

      {field.type === 'voice' && (
        <PropertySection icon={Mic} title="Voice Recording" description="Captured on mobile. The server uploads the audio to storage on sync.">
          <PropertyField>
            <InputCompact>
              <Input
                label="Placeholder Text"
                value={props.placeholder || ''}
                onChange={(e) => handlePropChange('placeholder', e.target.value)}
                placeholder="Tap to record"
              />
            </InputCompact>
          </PropertyField>
          <PropertyField label="Max Duration (seconds)" help="Recordings auto-stop at this limit (default 300 = 5 min).">
            <InputCompact>
              <Input
                type="number"
                min="5"
                value={props.maxDurationSeconds ?? 300}
                onChange={(e) => handlePropChange('maxDurationSeconds', parseInt(e.target.value, 10) || 300)}
                placeholder="300"
              />
            </InputCompact>
          </PropertyField>
          <PropertyField label="Recording Quality">
            <StyledSelect
              value={props.quality || 'medium'}
              onChange={(e) => handlePropChange('quality', e.target.value)}
            >
              <option value="low">Low (smaller file)</option>
              <option value="medium">Medium (recommended)</option>
              <option value="high">High (larger file)</option>
            </StyledSelect>
          </PropertyField>
          <BooleanRow
            label="Enable transcription"
            description="Turn recordings into readable text. The transcript shows up under each recording and anywhere else the form data appears."
            checked={props.enableTranscription !== false}
            onChange={(next) => handlePropChange('enableTranscription', next)}
          />
          {props.enableTranscription !== false && (
            <PropertyField label="Transcription Language">
              <StyledSelect
                value={props.transcriptionLanguage || 'en-US'}
                onChange={(e) => handlePropChange('transcriptionLanguage', e.target.value)}
              >
                <option value="en-US">English (US)</option>
                <option value="en-GB">English (UK)</option>
                <option value="en-CA">English (Canada)</option>
                <option value="fr-FR">French (France)</option>
                <option value="fr-CA">French (Canada)</option>
                <option value="es-US">Spanish (US)</option>
                <option value="ar-SA">Arabic</option>
                <option value="tr-TR">Turkish</option>
                <option value="de-DE">German</option>
                <option value="auto">Auto-detect</option>
              </StyledSelect>
            </PropertyField>
          )}
        </PropertySection>
      )}

      {(field.type === 'qr_scanner' || field.type === 'barcode_scanner') && (
        <PropertySection
          icon={ShieldCheck}
          title="Validation Rules"
          description="Add one or more rules that the scanned value must satisfy."
        >
          <ValidationRuleEditor
            rules={props.validationRules?.rules || []}
            onChange={(nextRules) => handlePropChange('validationRules', { ...(props.validationRules || {}), rules: nextRules })}
          />
          <BooleanRow
            label="Allow empty values"
            description="If the field isn't required, an empty value passes validation."
            checked={props.validationRules?.allowEmpty !== false}
            onChange={(next) => handlePropChange('validationRules', { ...(props.validationRules || {}), allowEmpty: next })}
          />
        </PropertySection>
      )}

      {field.type === 'barcode_scanner' && (
        <PropertySection icon={Barcode} title="Barcode Format" description="Restrict the scanner to a specific format (mobile only).">
          <PropertyField label="Format">
            <StyledSelect value={props.barcodeFormat || 'any'} onChange={(e) => handlePropChange('barcodeFormat', e.target.value)}>
              <option value="any">Any Format</option>
              <option value="ean13">EAN-13</option>
              <option value="ean8">EAN-8</option>
              <option value="upc">UPC</option>
              <option value="code128">Code 128</option>
              <option value="code39">Code 39</option>
              <option value="itf">ITF</option>
              <option value="codabar">Codabar</option>
            </StyledSelect>
          </PropertyField>
        </PropertySection>
      )}

      {field.componentId === 'sample_id' && (
        <LabIntegrationSection
          sampleIdField={field}
          allFields={allFields}
          onFieldsReplace={onFieldsReplace}
          analyteDraft={analyteDraft}
          setAnalyteDraft={setAnalyteDraft}
          analyteUnitDraft={analyteUnitDraft}
          setAnalyteUnitDraft={setAnalyteUnitDraft}
          analyteTypeDraft={analyteTypeDraft}
          setAnalyteTypeDraft={setAnalyteTypeDraft}
          analyteSuggestions={analyteSuggestions}
          onUpdate={onUpdate}
        />
      )}

      {props.labResultSource && (
        <PropertySection
          icon={FlaskConical}
          title="Lab Result Field"
          description="Analyte and unit are managed on the Sample ID field's expected lab results list. Values auto-fill when a matching lab report is linked."
        >
          <PropertyField label="Analyte">
            <InputCompact>
              <Input value={props.labResultSource.analyteName || ''} readOnly disabled />
            </InputCompact>
          </PropertyField>
          <PropertyField label="Unit">
            <InputCompact>
              <Input value={props.labResultSource.unitHint || '—'} readOnly disabled />
            </InputCompact>
          </PropertyField>
        </PropertySection>
      )}

      {field.type === 'repeated_form' && (
        <>
          <PropertySection icon={Repeat} title="Subform" description="Choose a form template and how many instances are allowed.">
            <PropertyField label="Subform Template" help="Pick which form to use as the repeated template.">
              <StyledSelect
                value={props.configuration?.repeatedFormSchemaId || ''}
                onChange={(e) => handleConfigChange({ repeatedFormSchemaId: e.target.value ? parseInt(e.target.value) : null })}
              >
                <option value="">Select a form...</option>
                {repeatedFormSchemas
                  .filter(schema => !currentFormId || schema.id !== currentFormId)
                  .map(schema => (
                    <option key={schema.id} value={schema.id}>{schema.name}</option>
                  ))}
              </StyledSelect>
            </PropertyField>

            <PropertyField label="Display Name Override" optional help="Leave blank to use the template's name.">
              <InputCompact>
                <Input
                  value={props.configuration?.repeatedFormDisplayName || ''}
                  onChange={(e) => handleConfigChange({ repeatedFormDisplayName: e.target.value })}
                  placeholder="e.g. Samples"
                />
              </InputCompact>
            </PropertyField>

            <InlineRow>
              <PropertyField label="Minimum instances" help="0 = optional">
                <InputCompact>
                  <Input
                    type="number"
                    min="0"
                    value={props.configuration?.minInstances ?? 0}
                    onChange={(e) => handleConfigChange({ minInstances: parseInt(e.target.value) || 0 })}
                  />
                </InputCompact>
              </PropertyField>
              <PropertyField label="Maximum instances">
                <InputCompact>
                  <Input
                    type="number"
                    min="1"
                    value={props.configuration?.maxInstances ?? 10}
                    onChange={(e) => handleConfigChange({ maxInstances: parseInt(e.target.value) || 10 })}
                  />
                </InputCompact>
              </PropertyField>
            </InlineRow>
          </PropertySection>

          <PropertySection
            icon={MousePointerClick}
            title="Entry Modes"
            description="Control how users add child submissions. If both are off, this field rejects all instances."
          >
            <BooleanRow
              label="Allow creating new submissions"
              description="Users can create a brand-new child submission inline."
              checked={props.configuration?.allowCreateNew !== false}
              onChange={(next) => handleConfigChange({ allowCreateNew: next })}
            />
            <BooleanRow
              label="Allow linking existing submissions"
              description="Users can pick from existing submissions of the template form."
              checked={props.configuration?.allowLinkExisting !== false}
              onChange={(next) => handleConfigChange({ allowLinkExisting: next })}
            />
          </PropertySection>
        </>
      )}

      {/* Field Rules — show/hide, cascade, and colour rules driven by other fields */}
      {showFieldRules && (
        <PropertySection
          icon={GitBranch}
          title="Field Rules"
          description="Show, hide, filter, or colour this field based on other answers."
          collapsible
          defaultOpen={false}
        >
          <FieldRulesEditor
            rules={props.rules || []}
            siblingFields={ruleSiblingFields}
            currentField={field}
            onChange={(next) => handlePropChange('rules', next)}
          />
        </PropertySection>
      )}

      {/* Advanced — always last, collapsed by default */}
      {showAdvanced && (
        <PropertySection
          icon={Settings2}
          title="Advanced"
          collapsible
          defaultOpen={false}
        >
          <BooleanRow
            label="Hide during submission fill"
            description="Field stays out of the fill form but still shows up in filters, analytics, and submission detail views."
            checked={!!props.isHidden}
            onChange={(next) => handlePropChange('isHidden', next)}
          />
        </PropertySection>
      )}
    </PropertiesContainer>
  );
};

// ── Lab Integration section (SampleId fields only) ──────────────────────────

const LabSectionBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
`;

const ExpectedResultsLabel = styled.div`
  font-size: ${props => props.theme.typography.sizes.small};
  font-weight: ${props => props.theme.typography.weights.semiBold};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${props => props.theme.colors.muted};
`;

const AnalyteList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
`;

const AnalyteRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${props => props.theme.spacing.sm};
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.md};
  border: 1px solid ${props => props.theme.colors.border};
  background: ${props => props.theme.colors.background};
`;

const AnalyteMain = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
`;

const AnalyteNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  min-width: 0;
`;

const AnalyteName = styled.span`
  font-weight: ${props => props.theme.typography.weights.semiBold};
  font-size: ${props => props.theme.typography.sizes.default};
  color: ${props => props.theme.colors.text};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TypeBadge = styled.span`
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 2px ${props => props.theme.spacing.xs};
  border-radius: ${props => props.theme.borderRadius.sm};
  background: ${props => props.theme.colors.backgroundSecondary};
  color: ${props => props.theme.colors.tint};
  border: 1px solid ${props => props.theme.colors.tint};
  flex-shrink: 0;
`;

const AnalyteUnit = styled.span`
  color: ${props => props.theme.colors.muted};
  font-size: ${props => props.theme.typography.sizes.small};
`;

const RemoveBtn = styled.button`
  border: 1px solid transparent;
  background: transparent;
  padding: ${props => props.theme.spacing.xs};
  cursor: pointer;
  color: ${props => props.theme.colors.muted};
  display: inline-flex;
  align-items: center;
  border-radius: ${props => props.theme.borderRadius.sm};
  transition: all 0.15s ease;

  &:hover {
    background: ${props => props.theme.colors.danger}15;
    color: ${props => props.theme.colors.danger};
    border-color: ${props => props.theme.colors.danger}40;
  }
`;

const EmptyNote = styled.div`
  padding: ${props => props.theme.spacing.md};
  border: 1px dashed ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  color: ${props => props.theme.colors.muted};
  font-size: ${props => props.theme.typography.sizes.small};
  text-align: center;
  font-style: italic;
`;

const ResultTypeGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing.sm};
`;

const ResultTypeButton = styled.button`
  padding: ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.md};
  border: 1px solid ${props => props.$active ? props.theme.colors.tint : props.theme.colors.border};
  background: ${props => props.$active ? `${props.theme.colors.tint}15` : props.theme.colors.background};
  color: ${props => props.$active ? props.theme.colors.tint : props.theme.colors.text};
  cursor: pointer;
  text-align: left;
  transition: all 0.15s ease;

  & > .label {
    font-weight: ${props => props.theme.typography.weights.semiBold};
    font-size: ${props => props.theme.typography.sizes.small};
  }

  & > .hint {
    font-size: 11px;
    color: ${props => props.theme.colors.muted};
    margin-top: 2px;
  }
`;

const AddAnalyteBar = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
  padding-top: ${props => props.theme.spacing.md};
  border-top: 1px solid ${props => props.theme.colors.border};
`;

const LabIntegrationSection = ({
  sampleIdField,
  allFields,
  onFieldsReplace,
  analyteDraft,
  setAnalyteDraft,
  analyteUnitDraft,
  setAnalyteUnitDraft,
  analyteTypeDraft,
  setAnalyteTypeDraft,
  analyteSuggestions,
  onUpdate,
}) => {
  const expected = Array.isArray(sampleIdField.props.expectedLabResults)
    ? sampleIdField.props.expectedLabResults
    : [];

  const expectedLower = useMemo(
    () => new Set(expected.map((a) => a.name?.toLowerCase()).filter(Boolean)),
    [expected]
  );

  const commitExpected = (nextExpected) => {
    const updatedSampleField = {
      ...sampleIdField,
      props: { ...sampleIdField.props, expectedLabResults: nextExpected },
    };

    if (typeof onFieldsReplace === 'function' && Array.isArray(allFields)) {
      const replacedSampleField = allFields.map((f) => (f.id === sampleIdField.id ? updatedSampleField : f));
      const nextFields = synthesizeLabResultFields(updatedSampleField, nextExpected, replacedSampleField);
      onFieldsReplace(nextFields);
    } else {
      onUpdate(updatedSampleField);
    }
  };

  const addAnalyte = (name, unit, type) => {
    const trimmed = String(name || '').trim();
    if (!trimmed) return;
    if (expectedLower.has(trimmed.toLowerCase())) {
      setAnalyteDraft('');
      setAnalyteUnitDraft('');
      return;
    }
    const resolvedType = type === 'Text' ? 'Text' : 'Number';
    const next = [
      ...expected,
      { name: trimmed, ...(unit ? { unit: String(unit).trim() } : {}), type: resolvedType },
    ];
    commitExpected(next);
    setAnalyteDraft('');
    setAnalyteUnitDraft('');
    setAnalyteTypeDraft('Number');
  };

  const removeAnalyte = (name) => {
    commitExpected(expected.filter((a) => a.name !== name));
  };

  const unlistedSuggestions = (analyteSuggestions || []).filter(
    (s) => s?.name && !expectedLower.has(s.name.toLowerCase())
  );

  const canAdd = analyteDraft.trim().length > 0;

  return (
    <PropertySection
      icon={FlaskConical}
      title="Lab Integration"
      description="Declare the analytes the lab will report. We'll auto-create hidden fields that fill in when a lab report is linked."
    >
      <LabSectionBody>
        <ExpectedResultsLabel>
          Expected Results ({expected.length})
        </ExpectedResultsLabel>

        {expected.length > 0 ? (
          <AnalyteList>
            {expected.map((a) => (
              <AnalyteRow key={a.name}>
                <AnalyteMain>
                  <AnalyteNameRow>
                    <AnalyteName>{a.name}</AnalyteName>
                    {a.type === 'Text' && <TypeBadge>Text</TypeBadge>}
                  </AnalyteNameRow>
                  {a.unit && <AnalyteUnit>{a.unit}</AnalyteUnit>}
                </AnalyteMain>
                <RemoveBtn
                  type="button"
                  onClick={() => removeAnalyte(a.name)}
                  aria-label={`Remove ${a.name}`}
                  title="Remove"
                >
                  <XIcon size={16} />
                </RemoveBtn>
              </AnalyteRow>
            ))}
          </AnalyteList>
        ) : (
          <EmptyNote>No analytes declared yet.</EmptyNote>
        )}

        <AddAnalyteBar>
          <PropertyField>
            <InputCompact>
              <Input
                label="Analyte name"
                value={analyteDraft}
                onChange={(e) => setAnalyteDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addAnalyte(analyteDraft, analyteUnitDraft, analyteTypeDraft);
                  }
                }}
                placeholder="e.g. E. Coli"
                autoComplete="off"
                list="lab-analyte-suggestions"
              />
            </InputCompact>
            <datalist id="lab-analyte-suggestions">
              {unlistedSuggestions.map((s) => (
                <option key={s.name} value={s.name}>
                  {s.units ? `${s.units} · used ${s.usageCount}×` : `used ${s.usageCount}×`}
                </option>
              ))}
            </datalist>
          </PropertyField>

          <PropertyField>
            <InputCompact>
              <Input
                label="Unit"
                value={analyteUnitDraft}
                onChange={(e) => setAnalyteUnitDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addAnalyte(analyteDraft, analyteUnitDraft, analyteTypeDraft);
                  }
                }}
                placeholder="e.g. mg/L (optional)"
              />
            </InputCompact>
          </PropertyField>

          <PropertyField label="Result type">
            <ResultTypeGrid>
              {[
                { value: 'Number', label: 'Numeric', hint: 'e.g. 7.2 mg/L' },
                { value: 'Text', label: 'Qualitative', hint: 'e.g. Absent / NA' },
              ].map((opt) => (
                <ResultTypeButton
                  key={opt.value}
                  type="button"
                  $active={analyteTypeDraft === opt.value}
                  onClick={() => setAnalyteTypeDraft(opt.value)}
                >
                  <div className="label">{opt.label}</div>
                  <div className="hint">{opt.hint}</div>
                </ResultTypeButton>
              ))}
            </ResultTypeGrid>
          </PropertyField>

          <Button
            type="button"
            variant="secondary"
            onClick={() => addAnalyte(analyteDraft, analyteUnitDraft, analyteTypeDraft)}
            disabled={!canAdd}
            style={{ width: '100%', justifyContent: 'center', opacity: canAdd ? 1 : 0.5 }}
          >
            <PlusIcon size={14} style={{ marginRight: 4 }} />
            Add analyte
          </Button>
        </AddAnalyteBar>
      </LabSectionBody>
    </PropertySection>
  );
};
